import DjangoMessages from 'components/DjangoMessages';
import Field from 'components/Form/Field';
import Icon from 'components/Icon';
import KeyValue from 'components/Layout/KeyValue';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TooltipWrapper from 'components/Tooltip';
import Value from 'components/Value';
import {ComponentProps} from 'react';
import {useBackend} from 'state/backend';
import {c, jt, t} from 'ttag';
import {setUrl} from 'utils/request';

import {BackendData} from '../types';
import {sendingSettingsLinks} from '../utils';

const SendingMethods = () => {
    const {data} = useBackend<BackendData>();

    const value = [
        {label: t`Send via Client Inbox`, value: data.sendToClientinboxDefault},
        {label: t`Secure Send`, value: data.sendBySecureSendDefault},
        {label: t`Send by Post`, value: data.sendByPostDefault},
    ];
    if (data.isUsingPeppolSend) {
        value.push({
            label: t`Peppol Send`,
            value: data.sendByPeppolsendDefault,
        });
    }

    const link = (
        <Link
            href={sendingSettingsLinks.howToSendDocuments}
            isOpenInNewTab={true}
            key="help-center"
        >
            {t`Help Center`}
        </Link>
    );

    return (
        <>
            <Field label={t`Sending Methods`} type="value">
                <Value
                    value={{
                        type: 'boolean-group',
                        value: value,
                    }}
                />
            </Field>
            <Field label="" type="value">
                <Value
                    value={{
                        type: 'custom',
                        value: (
                            <div className="text-light-secondary">{jt`Send by Post requires the purchase of MailPoints. See the ${link} (Japanese) for more information on our Sending Methods.`}</div>
                        ),
                    }}
                />
            </Field>
        </>
    );
};

const ClientInboxUserEditing = () => {
    const {data, flags} = useBackend<BackendData>();

    if (!flags.portal_allow_clientinbox_users_to_edit_users) return null;

    return (
        <Field label={t`Client Inbox User Editing`} type="value">
            <Value
                value={{
                    label: t`Allow Client to Edit Client Inbox Users`,
                    type: 'boolean',
                    value: data.defaultAllowClientinboxUsersToEditUsers,
                }}
            />
        </Field>
    );
};

const PasswordProtection = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`Password Protection`} type="value">
            <Value
                value={{
                    label: t`Send Separate Password Email`,
                    type: 'boolean',
                    value: data.pwProtectPickupPages,
                }}
            />
        </Field>
    );
};

const MakeleapsPayments = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`MakeLeaps Payments`} type="value">
            <Value
                value={{
                    label: t`Enable MakeLeaps Payments`,
                    type: 'boolean',
                    value: data.enableCcPayments,
                }}
            />
        </Field>
    );
};

const ContactsForSecureSending = () => {
    const {data} = useBackend<BackendData>();
    return (
        <Field label={t`Contacts for Secure Sending`} type="value">
            <Value
                value={{
                    type: 'string',
                    value: data.useDocumentContact
                        ? t`Document's Contacts`
                        : t`Client's Contacts`,
                }}
            />
        </Field>
    );
};

const DefaultSecureSendSubject = () => {
    const {data} = useBackend<BackendData>();
    return (
        <>
            <Field label={t`Default Secure Send Subject`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.securesendSubject || '—',
                    }}
                />
            </Field>
            <Field label={t`Default Secure Send Message`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.securesendMessage || '—',
                    }}
                />
            </Field>
        </>
    );
};

const PeppolSend = () => {
    const {data} = useBackend<BackendData>();

    if (!data.isUsingPeppolSend) return null;

    return (
        <>
            <Field label={t`Bank Account Number`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.peppolBankAccount || '—',
                    }}
                />
            </Field>
            <Field label={t`Bank Account Name`} type="value">
                <Value
                    value={{
                        type: 'string',
                        value: data.peppolBankAccountName || '—',
                    }}
                />
            </Field>
        </>
    );
};

const DocumentArchive = () => {
    const {data} = useBackend<BackendData>();
    const createDocumentArchiveForMarkedAsSent =
        data.createDocumentArchiveForMarkedAsSent || false;

    return (
        <Field
            helpText={t`You can set a default for Document Archive Creation when you update the Document Sent Date via UI.`}
            label={t`Default when updating Document Sent Date`}
            type="value"
        >
            <Value
                value={{
                    type: 'custom',
                    value: (
                        <div className="flex flex-col gap-2">
                            <div className="inline-flex items-center gap-2">
                                <Icon
                                    icon={
                                        createDocumentArchiveForMarkedAsSent
                                            ? 'enabled_checkbox'
                                            : 'disabled_checkbox'
                                    }
                                    size="base"
                                />
                                {t`Create Document Archive`}
                            </div>
                            {data.manualArchiveEnabled.allowed &&
                                createDocumentArchiveForMarkedAsSent && (
                                    <div className="inline-flex items-center gap-2 border-l border-l-grey-300 pl-2">
                                        <Icon
                                            icon="enabled_checkbox"
                                            size="base"
                                        />
                                        {data.defaultMarkedAsSentArchiveSource ===
                                            'manually_sent_electronically' &&
                                            t`Sent electronically outside MakeLeaps`}
                                        {data.defaultMarkedAsSentArchiveSource ===
                                            'manually_sent_physically' &&
                                            t`Sent paper copy outside MakeLeaps`}
                                        {!data.defaultMarkedAsSentArchiveSource &&
                                            t`No default sending method selected`}
                                    </div>
                                )}
                        </div>
                    ),
                }}
            />
        </Field>
    );
};
const EmailSettings = () => {
    const {data} = useBackend<BackendData>();
    const link = (
        <Link href={sendingSettingsLinks.customEmailDomain} key="help-center">
            {t`Help Center`}
        </Link>
    );
    return (
        <>
            <Field label={t`Custom Email Domains`} type="value">
                <Value
                    value={{
                        type: 'custom',
                        value: (
                            <div className="flex flex-col gap-4">
                                <div className="inline-flex items-center">
                                    <div className="flex-1">
                                        {data.verifiedCustomDomains.length ===
                                        0 ? (
                                            '—'
                                        ) : (
                                            <div className="flex flex-col gap-2">
                                                {data.verifiedCustomDomains.map(
                                                    (domain) => (
                                                        <div key={domain}>
                                                            {domain}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="h-full">
                                        <TooltipWrapper
                                            message={
                                                data.permissions
                                                    .companySettingsCanEdit
                                                    .failureReason
                                            }
                                        >
                                            <LinkButton
                                                disabled={
                                                    !data.permissions
                                                        .companySettingsCanEdit
                                                        .allowed
                                                }
                                                href={
                                                    sendingSettingsLinks.partnerCustomEmailDomainListView
                                                }
                                                kind="secondary"
                                                size="xs"
                                            >{t`Manage Custom Domains`}</LinkButton>
                                        </TooltipWrapper>
                                    </div>
                                </div>
                                <div className="text-light-secondary">{jt`Send emails from your company's email domain(s). Details in ${link}.`}</div>
                            </div>
                        ),
                    }}
                />
            </Field>
            <Field label={t`Default Reply-To Address`} type="value">
                <Value
                    value={{
                        type: 'custom',
                        value: (
                            <div className="flex flex-col gap-4">
                                <div className="inline-flex items-center">
                                    {data.defaultReplyToContact?.label || '—'}
                                </div>
                                <div className="text-light-secondary">{t`Set a company contact as the default Reply-To address for outgoing emails.`}</div>
                                <div></div>
                            </div>
                        ),
                    }}
                />
            </Field>
        </>
    );
};
const Page = () => {
    const {data} = useBackend<BackendData>();

    const clientListLink = (
        <a href={sendingSettingsLinks.clientListView}>{t`client list`}</a>
    );

    const editDocumentSendingLink: ComponentProps<
        typeof SectionHeader
    >['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.documentSendingSettingsEdit)
            : () => {},
    };

    const editPeppolSendLink: ComponentProps<typeof SectionHeader>['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.partnerPeppolEditView)
            : () => {},
    };

    const editDocumentArchiveLink: ComponentProps<
        typeof SectionHeader
    >['link'] = {
        disabled: !data.permissions.companySettingsCanEdit.allowed,
        icon: 'edit',
        kind: 'secondary',
        label: t`Edit`,
        onClick: data.permissions.companySettingsCanEdit.allowed
            ? () => setUrl(sendingSettingsLinks.documentArchiveEditView)
            : () => {},
    };

    const editEmailSettingsLink: ComponentProps<typeof SectionHeader>['link'] =
        {
            disabled: !data.permissions.companySettingsCanEdit.allowed,
            icon: 'edit',
            kind: 'secondary',
            label: t`Edit`,
            onClick: data.permissions.companySettingsCanEdit.allowed
                ? () =>
                      setUrl(sendingSettingsLinks.documentEmailSettingsEditView)
                : () => {},
        };

    return (
        <div>
            <DjangoMessages className="mb-4" />
            <NavigationTabs.Partner selection="sending_settings" />
            <div>
                <SectionHeader
                    link={editDocumentSendingLink}
                    subtitle={jt`These settings are used as the default values when adding new clients. You can see or edit these settings for existing clients in the ${clientListLink}.`}
                    title={t`Document Sending`}
                    tooltipMessage={
                        data.permissions.companySettingsCanEdit.failureReason
                    }
                />
                <KeyValue className="mt-4" keyWidth={14}>
                    <SendingMethods />
                    <ClientInboxUserEditing />
                    <PasswordProtection />
                    <MakeleapsPayments />
                    <ContactsForSecureSending />
                    <DefaultSecureSendSubject />
                </KeyValue>
                {data.isUsingPeppolSend && (
                    <>
                        <SectionHeader
                            link={editPeppolSendLink}
                            title={t`Peppol Send`}
                            tooltipMessage={
                                data.permissions.companySettingsCanEdit
                                    .failureReason
                            }
                        />
                        <KeyValue className="mt-4" keyWidth={14}>
                            <PeppolSend />
                        </KeyValue>
                    </>
                )}
                <SectionHeader
                    link={editDocumentArchiveLink}
                    title={t`Document Archive`}
                    tooltipMessage={
                        data.permissions.companySettingsCanEdit.failureReason
                    }
                />
                <KeyValue className="mt-4" keyWidth={14}>
                    <DocumentArchive />
                </KeyValue>
                <SectionHeader
                    link={editEmailSettingsLink}
                    title={c('document-settings').t`Email Settings`}
                    tooltipMessage={
                        data.permissions.companySettingsCanEdit.failureReason
                    }
                />
                <KeyValue className="mt-4" keyWidth={14}>
                    <EmailSettings />
                </KeyValue>
            </div>
        </div>
    );
};
export default Page;
